// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  ICertificationWebpageSelfAssessmentGetDetailAttrs,
  ICertificationWebpageSelfAssessmentGetListAttrs,
  ICertificationWebpageSelfAssessmentUpdateUserAnswerAttrs,
  ICertificationWebpageSelfAssessmentUploadDocumentAttrs,
  ICertificationWebpageSelfAssessmentDeleteDocumentAttrs,
  ICertificationWebpageSelfAssessmentDownloadDocumentAttrs,
} from 'interfaces/webpage/certification/registration/self-assessment/self-assessment-view.interface'

const useCertificationSelfAssessment = () => {
  // Hook
  const {
    certificationWebpageSelfAssessment: {
      certificationWebpageSelfAssessment_loading,
      certificationWebpageSelfAssessment_list,
      certificationWebpageSelfAssessment_detail,
      certificationWebpageSelfAssessment_CLEAR_LIST,
      certificationWebpageSelfAssessment_CLEAR_DETAIL,
      certificationWebpageSelfAssessment_MAP_ANSWER_FILE_DETAIL,
      certificationWebpageSelfAssessment_fetchList: fetchList,
      certificationWebpageSelfAssessment_fetchDetail: fetchDetail,
      certificationWebpageSelfAssessment_update: update,
      certificationWebpageSelfAssessment_uploadDocument: upload,
      certificationWebpageSelfAssessment_deleteDocument: deleteDocument,
      certificationWebpageSelfAssessment_downloadDocument: downloadDocument,
      certificationWebpageSelfAssessment_previewDocument: previewDocument
    },
  } = useStores()

  /**
   * @description Fetch self assessment list
   *
   * @param {ICertificationWebpageSelfAssessmentGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageSelfAssessment_fetchList = useCallback(
    (payload: ICertificationWebpageSelfAssessmentGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          requestConfig: { ...payload.requestConfig },
          params: { ...payload.params },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchList],
  )

  /**
   * @description Fetch self assessment detail
   *
   * @param {ICertificationWebpageSelfAssessmentGetDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageSelfAssessment_fetchDetail = useCallback(
    (payload: ICertificationWebpageSelfAssessmentGetDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDetail({
          requestConfig: { ...payload.requestConfig },
          body: { ...payload.body },
          params: { ...payload.params },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchDetail],
  )

  /**
   * @description Update self assessment user answer
   *
   * @param {ICertificationWebpageSelfAssessmentUpdateUserAnswerAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageSelfAssessment_update = useCallback(
    (payload: ICertificationWebpageSelfAssessmentUpdateUserAnswerAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        update({
          requestConfig: { ...payload.requestConfig },
          params: { ...payload.params },
          body: { ...payload.body },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [update],
  )

  /**
   * @description Upload self assessment competency
   *
   * @param {ICertificationWebpageSelfAssessmentUploadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageSelfAssessment_uploadDocument = useCallback(
    (payload: ICertificationWebpageSelfAssessmentUploadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        upload({
          requestConfig: { ...payload.requestConfig },
          params: { ...payload.params },
          body: { ...payload.body },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [upload],
  )

  /**
   * @description Delete self assessment competency document
   *
   * @param {ICertificationWebpageSelfAssessmentDeleteDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageSelfAssessment_deleteDocument = useCallback(
    (payload: ICertificationWebpageSelfAssessmentDeleteDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        deleteDocument({
          requestConfig: { ...payload.requestConfig },
          params: { ...payload.params },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [deleteDocument],
  )
  /**
   * @description Download document of self assessment
   *
   * @param {ICertificationWebpageSelfAssessmentDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageSelfAssessment_downloadDocument = useCallback(
    (payload: ICertificationWebpageSelfAssessmentDownloadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        downloadDocument({
          requestConfig: { ...payload.requestConfig },
          params: { ...payload.params },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [downloadDocument],
  )
  /**
   * @description Preview document of self assessment
   *
   * @param {ICertificationWebpageSelfAssessmentDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageSelfAssessment_previewDocument = useCallback(
    (payload: ICertificationWebpageSelfAssessmentDownloadDocumentAttrs): Promise<any> => {
      return new Promise((resolve, reject) => {
        previewDocument({
          requestConfig: { ...payload.requestConfig },
          params: { ...payload.params },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [previewDocument],
  )

  return {
    certificationWebpageSelfAssessment_loading,
    certificationWebpageSelfAssessment_list,
    certificationWebpageSelfAssessment_detail,
    certificationWebpageSelfAssessment_CLEAR_LIST,
    certificationWebpageSelfAssessment_CLEAR_DETAIL,
    certificationWebpageSelfAssessment_MAP_ANSWER_FILE_DETAIL,
    certificationWebpageSelfAssessment_fetchList,
    certificationWebpageSelfAssessment_fetchDetail,
    certificationWebpageSelfAssessment_update,
    certificationWebpageSelfAssessment_uploadDocument,
    certificationWebpageSelfAssessment_deleteDocument,
    certificationWebpageSelfAssessment_downloadDocument,
    certificationWebpageSelfAssessment_previewDocument
  }
}

export { useCertificationSelfAssessment }
